import React, { useEffect, useState } from "react";
import {
  FaArrowRight,
  FaBullhorn,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaGraduationCap,
  FaHardHat,
  FaPlay,
  FaTools,
  FaWrench,
  FaCertificate,
  FaBriefcase,
  FaBolt,
  FaUserTie,
  FaLeaf,
  FaSeedling,
  FaRecycle,
} from "react-icons/fa";
import api from "../api/api";

const HomeHeader = () => {
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await api.get("/api/slides/");

        const data = response.data?.results || response.data;

        setSlides(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load slides:", err);
        setError("Unable to load school information.");
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  useEffect(() => {
    if (slides.length <= 1 || isPaused) return;

    const timer = setInterval(() => {
      setCurrentIndex(
        (previous) => (previous + 1) % slides.length
      );
    }, 7500);

    return () => clearInterval(timer);
  }, [slides.length, isPaused]);

  const nextSlide = () => {
    if (!slides.length) return;

    setCurrentIndex(
      (previous) => (previous + 1) % slides.length
    );
  };

  const previousSlide = () => {
    if (!slides.length) return;

    setCurrentIndex(
      (previous) =>
        (previous - 1 + slides.length) % slides.length
    );
  };

  const selectSlide = (index) => {
    setCurrentIndex(index);
  };

  const pageStyles = `
    .vocational-header {
      --green: #16803c;
      --green-dark: #075e2d;
      --green-deep: #033d20;
      --green-light: #35b85a;
      --yellow: #f4c430;
      --yellow-light: #ffe37a;
      --black: #050806;
      --dark: #09100c;
      --dark-2: #101913;
      --gray: #8a9990;
      --light-gray: #dce7df;
      --white: #ffffff;

      position: relative;
      width: 100%;
      min-height: 720px;
      height: 88vh;
      overflow: hidden;
      background: var(--black);
      font-family:
        "Poppins",
        "Inter",
        Arial,
        sans-serif;
      isolation: isolate;
    }

    /* ==========================================================
       ENVIRONMENTAL BACKGROUND
    ========================================================== */

    .vocational-background {
      position: absolute;
      inset: 0;
      overflow: hidden;
      background:
        radial-gradient(
          circle at 75% 35%,
          rgba(22,128,60,0.20),
          transparent 35%
        ),
        linear-gradient(
          120deg,
          #020704 0%,
          #07140c 48%,
          #020704 100%
        );
    }

    .vocational-slide {
      position: absolute;
      inset: 0;
      opacity: 0;
      visibility: hidden;
      transition:
        opacity 1.2s ease,
        visibility 1.2s ease;
    }

    .vocational-slide.active {
      opacity: 1;
      visibility: visible;
    }

    .vocational-image {
      position: absolute;
      inset: 0;
      background-size: cover;
      background-position: center;
      transform: scale(1.07);
      transition:
        transform 10s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .vocational-slide.active .vocational-image {
      transform: scale(1);
    }

    .vocational-image::after {
      content: "";
      position: absolute;
      inset: 0;

      background:
        linear-gradient(
          90deg,
          rgba(2,10,5,0.96) 0%,
          rgba(2,10,5,0.88) 27%,
          rgba(3,35,18,0.62) 55%,
          rgba(3,35,18,0.18) 100%
        ),
        linear-gradient(
          0deg,
          rgba(0,0,0,0.60),
          transparent 45%,
          rgba(7,78,36,0.20)
        );
    }

    /* ==========================================================
       ENVIRONMENTAL ENERGY WAVES
    ========================================================== */

    .vocational-background::before {
      content: "";
      position: absolute;
      z-index: 2;
      width: 650px;
      height: 650px;
      right: -180px;
      top: -170px;
      border-radius: 50%;

      border:
        1px solid rgba(53,184,90,0.20);

      box-shadow:
        0 0 0 35px rgba(53,184,90,0.035),
        0 0 0 75px rgba(53,184,90,0.025),
        0 0 0 120px rgba(244,196,48,0.018);

      animation:
        environmentalPulse 9s ease-in-out infinite;
    }

    .vocational-background::after {
      content: "";
      position: absolute;
      z-index: 3;
      width: 900px;
      height: 280px;
      right: -250px;
      bottom: -40px;

      border-radius: 50%;

      border-top:
        2px solid rgba(53,184,90,0.28);

      transform: rotate(-18deg);

      filter: blur(1px);

      animation:
        energyFlow 8s ease-in-out infinite;
    }

    @keyframes environmentalPulse {
      0%,
      100% {
        transform:
          scale(0.94)
          rotate(0deg);
        opacity: 0.55;
      }

      50% {
        transform:
          scale(1.08)
          rotate(12deg);
        opacity: 1;
      }
    }

    @keyframes energyFlow {
      0%,
      100% {
        transform:
          translateX(40px)
          rotate(-18deg)
          scaleX(0.92);
        opacity: 0.25;
      }

      50% {
        transform:
          translateX(-80px)
          rotate(-12deg)
          scaleX(1.08);
        opacity: 0.65;
      }
    }

    /* ==========================================================
       FLOATING ENERGY PARTICLES
    ========================================================== */

    .vocational-grid {
      position: absolute;
      z-index: 7;
      inset: 0;
      opacity: 0.075;

      background-image:
        linear-gradient(
          rgba(255,255,255,0.55) 1px,
          transparent 1px
        ),
        linear-gradient(
          90deg,
          rgba(255,255,255,0.55) 1px,
          transparent 1px
        );

      background-size: 55px 55px;

      transform:
        perspective(600px)
        rotateX(58deg)
        scale(1.4);

      transform-origin: bottom;

      animation:
        environmentalGrid 18s linear infinite;

      pointer-events: none;
    }

    @keyframes environmentalGrid {
      from {
        background-position:
          0 0,
          0 0;
      }

      to {
        background-position:
          0 55px,
          55px 0;
      }
    }

    .vocational-energy-particles {
      position: absolute;
      z-index: 8;
      inset: 0;
      overflow: hidden;
      pointer-events: none;
    }

    .vocational-particle {
      position: absolute;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--green-light);
      box-shadow:
        0 0 12px rgba(53,184,90,0.75);

      animation:
        floatingParticle
        var(--duration)
        ease-in-out
        infinite;
      animation-delay: var(--delay);
    }

    .vocational-particle:nth-child(1) {
      left: 18%;
      bottom: 8%;
      --duration: 7s;
      --delay: -2s;
    }

    .vocational-particle:nth-child(2) {
      left: 35%;
      bottom: 20%;
      --duration: 9s;
      --delay: -5s;
    }

    .vocational-particle:nth-child(3) {
      left: 53%;
      bottom: 5%;
      --duration: 8s;
      --delay: -1s;
    }

    .vocational-particle:nth-child(4) {
      left: 72%;
      bottom: 15%;
      --duration: 10s;
      --delay: -6s;
      background: var(--yellow);
    }

    .vocational-particle:nth-child(5) {
      left: 83%;
      bottom: 25%;
      --duration: 7.5s;
      --delay: -3s;
    }

    .vocational-particle:nth-child(6) {
      left: 61%;
      bottom: 32%;
      --duration: 11s;
      --delay: -8s;
      background: var(--yellow-light);
    }

    @keyframes floatingParticle {
      0% {
        transform:
          translate3d(0, 50px, 0)
          scale(0.5);
        opacity: 0;
      }

      15% {
        opacity: 0.9;
      }

      50% {
        transform:
          translate3d(
            35px,
            -170px,
            0
          )
          scale(1);
        opacity: 0.75;
      }

      80% {
        opacity: 0.45;
      }

      100% {
        transform:
          translate3d(
            -20px,
            -350px,
            0
          )
          scale(0.25);
        opacity: 0;
      }
    }

    /* ==========================================================
       EXISTING SLIDING SHAPE
       SAME SHAPE CONCEPT — GREEN ENVIRONMENTAL COLOR
    ========================================================== */

    .vocational-orange-shape {
      position: absolute;
      z-index: 10;

      top: -120px;
      right: 15%;

      width: 260px;
      height: 900px;

      background:
        linear-gradient(
          180deg,
          rgba(53,184,90,0.96),
          rgba(7,94,45,0.96)
        );

      transform:
        rotate(17deg)
        translateY(0);

      opacity: 0.84;

      mix-blend-mode: multiply;

      box-shadow:
        0 0 80px rgba(22,128,60,0.20);

      animation:
        environmentalShapeMotion
        10s
        ease-in-out
        infinite;
    }

    @keyframes environmentalShapeMotion {
      0%,
      100% {
        transform:
          rotate(17deg)
          translateY(0)
          translateX(0);

        opacity: 0.76;
      }

      25% {
        transform:
          rotate(14deg)
          translateY(15px)
          translateX(-12px);

        opacity: 0.84;
      }

      50% {
        transform:
          rotate(19deg)
          translateY(-20px)
          translateX(15px);

        opacity: 0.92;
      }

      75% {
        transform:
          rotate(15deg)
          translateY(10px)
          translateX(-8px);

        opacity: 0.82;
      }
    }

    /* ==========================================================
       YELLOW ENERGY RING
    ========================================================== */

    .vocational-yellow-shape {
      position: absolute;
      z-index: 11;

      right: 5%;
      bottom: -180px;

      width: 360px;
      height: 360px;

      border-radius: 50%;

      border:
        55px solid rgba(244,196,48,0.55);

      box-shadow:
        0 0 70px rgba(244,196,48,0.12);

      animation:
        energyRing
        11s
        ease-in-out
        infinite;
    }

    @keyframes energyRing {
      0%,
      100% {
        transform:
          translateY(0)
          scale(0.95)
          rotate(0deg);

        opacity: 0.50;
      }

      50% {
        transform:
          translateY(-35px)
          scale(1.08)
          rotate(18deg);

        opacity: 0.82;
      }
    }

    /* ==========================================================
       TOP NAVIGATION
    ========================================================== */

    .vocational-top {
      position: absolute;
      z-index: 30;

      top: 0;
      left: 0;
      right: 0;

      height: 86px;

      display: flex;
      align-items: center;
      justify-content: space-between;

      padding: 0 5%;

      background:
        linear-gradient(
          90deg,
          rgba(2,10,5,0.96),
          rgba(5,22,12,0.88)
        );

      border-bottom:
        3px solid var(--green);

      box-shadow:
        0 5px 30px rgba(0,0,0,0.20);
    }

    .vocational-logo {
      display: flex;
      align-items: center;
      gap: 13px;
    }

    .vocational-logo-icon {
      width: 46px;
      height: 46px;

      display: flex;
      align-items: center;
      justify-content: center;

      color: white;

      background:
        linear-gradient(
          135deg,
          var(--green),
          var(--green-dark)
        );

      border:
        1px solid rgba(255,255,255,0.12);

      font-size: 21px;

      transform: rotate(-4deg);

      box-shadow:
        0 0 22px rgba(53,184,90,0.20);

      animation:
        iconEnergy 4s ease-in-out infinite;
    }

    @keyframes iconEnergy {
      0%,
      100% {
        transform:
          rotate(-4deg)
          translateY(0);
      }

      50% {
        transform:
          rotate(1deg)
          translateY(-3px);
      }
    }

    .vocational-logo-text {
      color: white;
    }

    .vocational-logo-text strong {
      display: block;

      font-size: 15px;
      font-weight: 900;

      letter-spacing: 0.08em;
    }

    .vocational-logo-text span {
      display: block;

      margin-top: 3px;

      color: #9bb4a2;

      font-size: 9px;
      font-weight: 700;

      letter-spacing: 0.18em;

      text-transform: uppercase;
    }

    .vocational-top-right {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .vocational-top-link {
      display: flex;
      align-items: center;
      gap: 7px;

      padding: 9px 13px;

      color: #e6f0e9;

      border:
        1px solid rgba(255,255,255,0.18);

      background:
        rgba(8,20,13,0.72);

      font-size: 10px;
      font-weight: 700;

      text-transform: uppercase;
      text-decoration: none;

      transition:
        0.3s ease;
    }

    .vocational-top-link svg {
      color: var(--green-light);
    }

    .vocational-top-link:hover {
      color: white;

      border-color:
        var(--green-light);

      background:
        var(--green-dark);

      transform:
        translateY(-2px);

      box-shadow:
        0 8px 24px rgba(22,128,60,0.20);
    }

    .vocational-top-link:hover svg {
      color: var(--yellow);
    }

    /* ==========================================================
       MAIN CONTENT
    ========================================================== */

    .vocational-main {
      position: relative;
      z-index: 15;

      height: 100%;

      display: flex;
      align-items: center;

      padding:
        125px
        5%
        90px;
    }

    .vocational-copy {
      width: min(670px, 58%);

      color: white;

      animation:
        environmentalEntry
        1.1s
        cubic-bezier(0.22,1,0.36,1)
        both;
    }

    @keyframes environmentalEntry {
      from {
        opacity: 0;
        transform:
          translateX(-45px)
          translateY(12px);
        filter: blur(4px);
      }

      to {
        opacity: 1;
        transform:
          translateX(0)
          translateY(0);
        filter: blur(0);
      }
    }

    .vocational-tag {
      display: inline-flex;
      align-items: center;
      gap: 8px;

      margin-bottom: 22px;

      padding: 9px 13px;

      color: white;

      background:
        linear-gradient(
          135deg,
          var(--green),
          var(--green-dark)
        );

      border-left:
        4px solid var(--yellow);

      font-size: 10px;
      font-weight: 900;

      letter-spacing: 0.13em;

      text-transform: uppercase;

      box-shadow:
        0 8px 25px rgba(0,0,0,0.20);
    }

    .vocational-tag svg {
      color: var(--yellow);
      font-size: 12px;
    }

    .vocational-title {
      margin: 0;

      color: white;

      font-size:
        clamp(44px, 6vw, 82px);

      line-height: 0.95;

      font-weight: 950;

      letter-spacing: -0.055em;

      text-transform: uppercase;

      text-shadow:
        0 8px 30px rgba(0,0,0,0.28);
    }

    .vocational-title-accent {
      display: block;
      color: var(--green-light);
    }

    .vocational-line {
      width: 120px;
      height: 7px;

      margin:
        25px 0 23px;

      background:
        linear-gradient(
          90deg,
          var(--green-light),
          var(--yellow)
        );

      box-shadow:
        0 0 18px rgba(53,184,90,0.25);
    }

    .vocational-description {
      max-width: 620px;

      margin: 0;

      color:
        rgba(255,255,255,0.78);

      font-size:
        clamp(14px, 1.3vw, 17px);

      line-height: 1.75;
    }

    /* ==========================================================
       BUTTONS
    ========================================================== */

    .vocational-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;

      margin-top: 30px;
    }

    .vocational-primary,
    .vocational-secondary {
      min-height: 52px;

      display: inline-flex;
      align-items: center;
      justify-content: center;

      gap: 11px;

      padding: 0 21px;

      text-decoration: none;

      font-size: 12px;
      font-weight: 900;

      text-transform: uppercase;

      transition:
        transform 0.3s ease,
        box-shadow 0.3s ease,
        background 0.3s ease;
    }

    .vocational-primary {
      color: white;

      background:
        linear-gradient(
          135deg,
          var(--green),
          var(--green-dark)
        );

      box-shadow:
        8px 8px 0
        rgba(244,196,48,0.72);
    }

    .vocational-primary:hover {
      transform:
        translate(3px,3px);

      box-shadow:
        4px 4px 0
        rgba(244,196,48,0.72);

      background:
        linear-gradient(
          135deg,
          var(--green-light),
          var(--green)
        );
    }

    .vocational-secondary {
      color: white;

      border:
        1px solid
        rgba(255,255,255,0.35);

      background:
        rgba(0,0,0,0.28);
    }

    .vocational-secondary:hover {
      transform:
        translateY(-3px);

      background: white;

      color: var(--black);
    }

    .vocational-play {
      width: 30px;
      height: 30px;

      display: flex;
      align-items: center;
      justify-content: center;

      color: white;

      background:
        var(--green);

      border-radius: 50%;

      font-size: 9px;

      box-shadow:
        0 0 15px
        rgba(53,184,90,0.35);
    }

    /* ==========================================================
       BENEFITS
    ========================================================== */

    .vocational-benefits {
      display: flex;
      flex-wrap: wrap;

      gap: 18px;

      margin-top: 28px;
    }

    .vocational-benefit {
      display: flex;
      align-items: center;

      gap: 7px;

      color:
        rgba(255,255,255,0.74);

      font-size: 10px;
      font-weight: 700;
    }

    .vocational-benefit svg {
      color: var(--green-light);

      filter:
        drop-shadow(
          0 0 6px
          rgba(53,184,90,0.35)
        );
    }

    /* ==========================================================
       RIGHT PANEL
    ========================================================== */

    .vocational-panel {
      position: absolute;
      z-index: 20;

      right: 5%;
      top: 50%;

      width: 360px;

      transform:
        translateY(-42%);

      animation:
        environmentalPanel
        1.2s
        0.15s
        cubic-bezier(0.22,1,0.36,1)
        both;
    }

    @keyframes environmentalPanel {
      from {
        opacity: 0;

        transform:
          translateY(-38%)
          translateX(55px)
          rotate(2deg);
      }

      to {
        opacity: 1;

        transform:
          translateY(-42%)
          translateX(0)
          rotate(0);
      }
    }

    .vocational-panel-main {
      overflow: hidden;

      border:
        1px solid
        rgba(255,255,255,0.16);

      background:
        linear-gradient(
          145deg,
          rgba(7,20,12,0.96),
          rgba(3,11,7,0.94)
        );

      box-shadow:
        20px 25px 70px
        rgba(0,0,0,0.50);

      backdrop-filter:
        blur(12px);
    }

    .vocational-panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      padding: 17px 20px;

      background:
        linear-gradient(
          90deg,
          var(--green-dark),
          var(--green)
        );

      color: white;

      border-bottom:
        2px solid var(--yellow);
    }

    .vocational-panel-header strong {
      font-size: 11px;
      font-weight: 900;

      letter-spacing: 0.08em;

      text-transform: uppercase;
    }

    .vocational-panel-header span {
      color: var(--yellow);

      font-size: 9px;
      font-weight: 900;

      text-transform: uppercase;
    }

    .vocational-panel-body {
      padding: 25px 22px;
    }

    .vocational-panel-icon {
      width: 58px;
      height: 58px;

      display: flex;
      align-items: center;
      justify-content: center;

      margin-bottom: 18px;

      color: white;

      background:
        linear-gradient(
          135deg,
          var(--green),
          var(--green-dark)
        );

      border:
        1px solid
        rgba(53,184,90,0.30);

      font-size: 25px;

      box-shadow:
        0 0 25px
        rgba(53,184,90,0.16);

      animation:
        panelIconFloat
        4.5s
        ease-in-out
        infinite;
    }

    @keyframes panelIconFloat {
      0%,
      100% {
        transform:
          translateY(0)
          rotate(0deg);
      }

      50% {
        transform:
          translateY(-5px)
          rotate(3deg);
      }
    }

    .vocational-panel-body h3 {
      margin: 0;

      color: white;

      font-size: 25px;

      line-height: 1.1;

      font-weight: 900;

      text-transform: uppercase;
    }

    .vocational-panel-body p {
      margin:
        11px 0 22px;

      color:
        #a8b7ad;

      font-size: 11px;

      line-height: 1.7;
    }

    .vocational-programs {
      display: grid;

      grid-template-columns:
        1fr 1fr;

      gap: 8px;
    }

    .vocational-program {
      min-height: 53px;

      display: flex;
      align-items: center;

      gap: 8px;

      padding: 8px 10px;

      border:
        1px solid
        rgba(255,255,255,0.10);

      color:
        #dce8df;

      background:
        rgba(255,255,255,0.035);

      font-size: 9px;
      font-weight: 700;

      transition:
        0.3s ease;
    }

    .vocational-program:hover {
      border-color:
        rgba(53,184,90,0.45);

      background:
        rgba(22,128,60,0.15);

      transform:
        translateY(-2px);
    }

    .vocational-program svg {
      flex-shrink: 0;

      color:
        var(--green-light);

      font-size: 13px;
    }

    .vocational-panel-footer {
      display: grid;

      grid-template-columns:
        repeat(3, 1fr);

      border-top:
        1px solid
        rgba(255,255,255,0.10);
    }

    .vocational-stat {
      padding: 17px 8px;

      text-align: center;

      border-right:
        1px solid
        rgba(255,255,255,0.10);
    }

    .vocational-stat:last-child {
      border-right: 0;
    }

    .vocational-stat strong {
      display: block;

      color:
        var(--green-light);

      font-size: 20px;

      font-weight: 950;
    }

    .vocational-stat span {
      display: block;

      margin-top: 3px;

      color: #829188;

      font-size: 8px;

      font-weight: 800;

      text-transform: uppercase;
    }

    /* ==========================================================
       ANNOUNCEMENT
    ========================================================== */

    .vocational-announcement {
      position: absolute;
      z-index: 25;

      right:
        calc(5% + 280px);

      bottom: 85px;

      width: 245px;

      padding: 15px;

      border-left:
        4px solid
        var(--green-light);

      background:
        rgba(4,14,8,0.94);

      box-shadow:
        10px 15px 35px
        rgba(0,0,0,0.30);

      backdrop-filter:
        blur(8px);
    }

    .vocational-announcement-top {
      display: flex;
      align-items: center;

      gap: 9px;

      margin-bottom: 9px;
    }

    .vocational-announcement-icon {
      width: 30px;
      height: 30px;

      display: flex;
      align-items: center;
      justify-content: center;

      color: white;

      background:
        var(--green);

      font-size: 12px;
    }

    .vocational-announcement small {
      color:
        var(--yellow);

      font-size: 8px;

      font-weight: 900;

      letter-spacing: 0.12em;

      text-transform: uppercase;
    }

    .vocational-announcement h4 {
      margin: 0;

      color: white;

      font-size: 11px;

      font-weight: 800;
    }

    .vocational-announcement p {
      margin: 0;

      color:
        #93a198;

      font-size: 9px;

      line-height: 1.55;
    }

    /* ==========================================================
       BOTTOM CONTROLS
    ========================================================== */

    .vocational-counter {
      position: absolute;
      z-index: 30;

      left: 5%;
      bottom: 29px;

      display: flex;
      align-items: center;

      gap: 10px;

      color: #8d9a91;

      font-size: 10px;

      font-weight: 800;
    }

    .vocational-counter strong {
      color:
        var(--green-light);

      font-size: 21px;
    }

    .vocational-counter-line {
      width: 45px;
      height: 2px;

      background:
        linear-gradient(
          90deg,
          var(--green),
          var(--yellow)
        );
    }

    .vocational-indicators {
      position: absolute;
      z-index: 30;

      left: 50%;
      bottom: 34px;

      display: flex;

      gap: 7px;

      transform:
        translateX(-50%);
    }

    .vocational-indicator {
      width: 28px;
      height: 4px;

      padding: 0;

      border: 0;

      background:
        rgba(255,255,255,0.25);

      cursor: pointer;

      transition:
        0.4s ease;
    }

    .vocational-indicator.active {
      width: 52px;

      background:
        linear-gradient(
          90deg,
          var(--green-light),
          var(--yellow)
        );

      box-shadow:
        0 0 10px
        rgba(53,184,90,0.35);
    }

    .vocational-controls {
      position: absolute;
      z-index: 30;

      right: 5%;
      bottom: 20px;

      display: flex;

      gap: 7px;
    }

    .vocational-arrow {
      width: 43px;
      height: 43px;

      display: flex;
      align-items: center;
      justify-content: center;

      border:
        1px solid
        rgba(255,255,255,0.25);

      color: white;

      background:
        rgba(4,15,8,0.82);

      cursor: pointer;

      transition:
        0.3s ease;
    }

    .vocational-arrow:hover {
      color: white;

      border-color:
        var(--green-light);

      background:
        var(--green-dark);

      transform:
        translateY(-3px);

      box-shadow:
        0 8px 20px
        rgba(22,128,60,0.25);
    }

    /* ==========================================================
       LOADING / ERROR
    ========================================================== */

    .vocational-loader {
      min-height: 650px;

      width: 100%;

      display: flex;
      align-items: center;
      justify-content: center;

      background:
        linear-gradient(
          135deg,
          #020704,
          #082513
        );

      color: white;

      font-family:
        "Poppins",
        Arial,
        sans-serif;
    }

    .vocational-loader-inner {
      text-align: center;
    }

    .vocational-loader-icon {
      width: 70px;
      height: 70px;

      display: flex;
      align-items: center;
      justify-content: center;

      margin:
        0 auto 20px;

      color: white;

      background:
        linear-gradient(
          135deg,
          var(--green),
          var(--green-dark)
        );

      border:
        1px solid
        rgba(255,255,255,0.12);

      font-size: 28px;

      animation:
        loaderFloat
        3s
        ease-in-out
        infinite;
    }

    @keyframes loaderFloat {
      0%,
      100% {
        transform:
          translateY(0)
          rotate(0deg);
      }

      50% {
        transform:
          translateY(-8px)
          rotate(5deg);
      }
    }

    .vocational-spinner {
      width: 28px;
      height: 28px;

      margin:
        0 auto 18px;

      border:
        3px solid
        rgba(255,255,255,0.15);

      border-top-color:
        var(--green-light);

      border-right-color:
        var(--yellow);

      border-radius: 50%;

      animation:
        vocationalSpin
        0.8s
        linear
        infinite;
    }

    @keyframes vocationalSpin {
      to {
        transform:
          rotate(360deg);
      }
    }

    .vocational-loader h3 {
      margin: 0;

      font-size: 18px;

      font-weight: 800;
    }

    .vocational-loader p {
      margin:
        7px 0 0;

      color:
        #829188;

      font-size: 11px;
    }

    .vocational-error-icon {
      width: 65px;
      height: 65px;

      display: flex;
      align-items: center;
      justify-content: center;

      margin:
        0 auto 20px;

      color: white;

      background:
        #b42318;

      font-size: 25px;

      font-weight: 900;
    }

    .vocational-retry {
      margin-top: 20px;

      padding:
        11px 22px;

      border: 0;

      color: white;

      background:
        var(--green);

      font-weight: 800;

      cursor: pointer;

      transition:
        0.3s ease;
    }

    .vocational-retry:hover {
      background:
        var(--green-light);

      transform:
        translateY(-2px);
    }

    /* ==========================================================
       TABLET
    ========================================================== */

    @media (max-width: 1100px) {
      .vocational-panel {
        width: 310px;
        right: 3%;
      }

      .vocational-copy {
        width: 64%;
      }

      .vocational-announcement {
        right:
          calc(3% + 240px);
      }

      .vocational-orange-shape {
        right: 12%;
      }
    }

    /* ==========================================================
       SMALL TABLET
    ========================================================== */

    @media (max-width: 850px) {
      .vocational-header {
        height: auto;
        min-height: 820px;
      }

      .vocational-main {
        min-height: 820px;

        align-items: flex-start;

        padding:
          130px
          5%
          120px;
      }

      .vocational-copy {
        width: 72%;
      }

      .vocational-panel {
        width: 285px;

        right: 3%;

        opacity: 0.88;
      }

      .vocational-announcement {
        display: none;
      }

      .vocational-orange-shape {
        right: 5%;
      }

      .vocational-top-link {
        display: none;
      }
    }

    /* ==========================================================
       MOBILE
    ========================================================== */

    @media (max-width: 650px) {
      .vocational-header {
        min-height: 760px;
      }

      .vocational-top {
        height: 72px;

        padding:
          0 20px;
      }

      .vocational-logo-icon {
        width: 39px;
        height: 39px;

        font-size: 17px;
      }

      .vocational-logo-text strong {
        font-size: 11px;
      }

      .vocational-logo-text span {
        font-size: 7px;
      }

      .vocational-main {
        min-height: 760px;

        padding:
          115px
          20px
          105px;
      }

      .vocational-copy {
        width: 100%;
      }

      .vocational-title {
        font-size:
          clamp(39px, 12vw, 58px);
      }

      .vocational-description {
        font-size: 13px;

        line-height: 1.7;
      }

      .vocational-panel {
        display: none;
      }

      .vocational-orange-shape {
        right: -110px;

        opacity: 0.25;
      }

      .vocational-yellow-shape {
        right: -160px;

        opacity: 0.25;
      }

      .vocational-actions {
        flex-direction: column;
        align-items: stretch;
      }

      .vocational-primary,
      .vocational-secondary {
        width: 100%;
      }

      .vocational-benefits {
        display: grid;

        grid-template-columns:
          1fr 1fr;

        gap: 13px;
      }

      .vocational-counter {
        left: 20px;
        bottom: 25px;
      }

      .vocational-controls {
        right: 20px;
        bottom: 18px;
      }

      .vocational-indicators {
        bottom: 75px;
      }

      .vocational-arrow {
        width: 39px;
        height: 39px;
      }

      .vocational-particle {
        opacity: 0.5;
      }
    }

    /* ==========================================================
       SMALL PHONE
    ========================================================== */

    @media (max-width: 400px) {
      .vocational-title {
        font-size: 37px;
      }

      .vocational-tag {
        font-size: 8px;
      }

      .vocational-benefits {
        grid-template-columns:
          1fr;
      }

      .vocational-indicators {
        display: none;
      }
    }

    /* ==========================================================
       REDUCED MOTION
    ========================================================== */

    @media (prefers-reduced-motion: reduce) {
      .vocational-slide,
      .vocational-image,
      .vocational-copy,
      .vocational-panel,
      .vocational-primary,
      .vocational-secondary,
      .vocational-arrow,
      .vocational-orange-shape,
      .vocational-yellow-shape,
      .vocational-particle,
      .vocational-grid,
      .vocational-background::before,
      .vocational-background::after,
      .vocational-logo-icon,
      .vocational-panel-icon {
        transition: none !important;
        animation: none !important;
      }
    }
  `;

  if (loading) {
    return (
      <>
        <style>{pageStyles}</style>

        <section className="vocational-loader">
          <div className="vocational-loader-inner">

            <div className="vocational-loader-icon">
              <FaLeaf />
            </div>

            <div className="vocational-spinner"></div>

            <h3>
              Preparing Your Training Experience
            </h3>

            <p>
              Loading vocational school information...
            </p>

          </div>
        </section>
      </>
    );
  }

  if (error) {
    return (
      <>
        <style>{pageStyles}</style>

        <section className="vocational-loader">
          <div className="vocational-loader-inner">

            <div className="vocational-error-icon">
              !
            </div>

            <h3>
              Unable to Load
            </h3>

            <p>
              {error}
            </p>

            <button
              className="vocational-retry"
              onClick={() =>
                window.location.reload()
              }
            >
              TRY AGAIN
            </button>

          </div>
        </section>
      </>
    );
  }

  if (!slides.length) {
    return (
      <>
        <style>{pageStyles}</style>

        <section className="vocational-loader">
          <div className="vocational-loader-inner">

            <div className="vocational-loader-icon">
              <FaSeedling />
            </div>

            <h3>
              Welcome to Our Trade School
            </h3>

            <p>
              Practical skills. Technical training.
              Real opportunities.
            </p>

          </div>
        </section>
      </>
    );
  }

  const currentSlide = slides[currentIndex];

  return (
    <>
      <style>{pageStyles}</style>

      <header
        className="vocational-header"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >

        {/* ======================================================
            BACKGROUND
        ====================================================== */}

        <div className="vocational-background">

          {slides.map((slide, index) => (
            <div
              key={slide.id || index}
              className={`vocational-slide ${
                currentIndex === index
                  ? "active"
                  : ""
              }`}
            >
              <div
                className="vocational-image"
                style={{
                  backgroundImage:
                    `url("${slide.image}")`,
                }}
              />
            </div>
          ))}

          {/* Existing main decorative shape */}
          <div className="vocational-orange-shape"></div>

          {/* Yellow environmental energy ring */}
          <div className="vocational-yellow-shape"></div>

          {/* Moving grid */}
          <div className="vocational-grid"></div>

          {/* Environmental energy particles */}
          <div className="vocational-energy-particles">
            <span className="vocational-particle"></span>
            <span className="vocational-particle"></span>
            <span className="vocational-particle"></span>
            <span className="vocational-particle"></span>
            <span className="vocational-particle"></span>
            <span className="vocational-particle"></span>
          </div>

        </div>

        {/* ======================================================
            TOP BAR
        ====================================================== */}

        <div className="vocational-top">

          <div className="vocational-logo">

            <div className="vocational-logo-icon">
              <FaLeaf />
            </div>

            <div className="vocational-logo-text">

              <strong>
                VOCATIONAL SCHOOL
              </strong>

              <span>
                Skills • Training • Opportunity
              </span>

            </div>

          </div>

          <div className="vocational-top-right">

            <a
              href="#programs"
              className="vocational-top-link"
            >
              <FaTools />
              Training Programs
            </a>

            <a
              href="#admissions"
              className="vocational-top-link"
            >
              <FaGraduationCap />
              Admissions
            </a>

          </div>

        </div>

        {/* ======================================================
            MAIN HERO
        ====================================================== */}

        <main className="vocational-main">

          <div
            className="vocational-copy"
            key={currentIndex}
          >

            <div className="vocational-tag">
              <FaLeaf />
              BUILD YOUR FUTURE
            </div>

            <h1 className="vocational-title">
              {currentSlide.title}
            </h1>

            <div className="vocational-line"></div>

            <p className="vocational-description">
              {currentSlide.description}
            </p>

            <div className="vocational-actions">

              <a
                href="#programs"
                className="vocational-primary"
              >
                Explore Programs
                <FaArrowRight />
              </a>

              <a
                href="#about"
                className="vocational-secondary"
              >
                <span className="vocational-play">
                  <FaPlay />
                </span>

                About Our School
              </a>

            </div>

            <div className="vocational-benefits">

              <div className="vocational-benefit">
                <FaCheckCircle />
                Hands-On Learning
              </div>

              <div className="vocational-benefit">
                <FaCheckCircle />
                Technical Training
              </div>

              <div className="vocational-benefit">
                <FaCheckCircle />
                Professional Skills
              </div>

              <div className="vocational-benefit">
                <FaCheckCircle />
                Career Preparation
              </div>

            </div>

          </div>

        </main>

        {/* ======================================================
            RIGHT TRAINING PANEL
        ====================================================== */}

        <aside className="vocational-panel">

          <div className="vocational-panel-main">

            <div className="vocational-panel-header">

              <strong>
                Training Center
              </strong>

              <span>
                Now Enrolling
              </span>

            </div>

            <div className="vocational-panel-body">

              <div className="vocational-panel-icon">
                <FaSeedling />
              </div>

              <h3>
                Learn A Skill.
                <br />
                Build A Career.
              </h3>

              <p>
                Our vocational programs combine
                practical instruction, technical
                knowledge and workplace preparation.
              </p>

              <div className="vocational-programs">

                <div className="vocational-program">
                  <FaWrench />
                  Technical Skills
                </div>

                <div className="vocational-program">
                  <FaHardHat />
                  Safety Training
                </div>

                <div className="vocational-program">
                  <FaCertificate />
                  Certification
                </div>

                <div className="vocational-program">
                  <FaBriefcase />
                  Career Skills
                </div>

                <div className="vocational-program">
                  <FaUserTie />
                  Professionalism
                </div>

                <div className="vocational-program">
                  <FaGraduationCap />
                  Assessment
                </div>

              </div>

            </div>

            <div className="vocational-panel-footer">

              <div className="vocational-stat">
                <strong>01</strong>
                <span>Learn</span>
              </div>

              <div className="vocational-stat">
                <strong>02</strong>
                <span>Practice</span>
              </div>

              <div className="vocational-stat">
                <strong>03</strong>
                <span>Master</span>
              </div>

            </div>

          </div>

        </aside>

        {/* ======================================================
            ANNOUNCEMENT
        ====================================================== */}

        <div className="vocational-announcement">

          <div className="vocational-announcement-top">

            <div className="vocational-announcement-icon">
              <FaBullhorn />
            </div>

            <div>

              <small>
                Important
              </small>

              <h4>
                Admissions Are Open
              </h4>

            </div>

          </div>

          <p>
            Applications are currently being accepted
            for vocational and technical training
            programs.
          </p>

        </div>

        {/* ======================================================
            SLIDE COUNTER
        ====================================================== */}

        {slides.length > 1 && (
          <div className="vocational-counter">

            <strong>
              {String(
                currentIndex + 1
              ).padStart(2, "0")}
            </strong>

            <div className="vocational-counter-line"></div>

            <span>
              {String(
                slides.length
              ).padStart(2, "0")}
            </span>

          </div>
        )}

        {/* ======================================================
            SLIDE INDICATORS
        ====================================================== */}

        {slides.length > 1 && (
          <div className="vocational-indicators">

            {slides.map((_, index) => (
              <button
                key={index}
                className={`vocational-indicator ${
                  currentIndex === index
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  selectSlide(index)
                }
                aria-label={
                  `Go to slide ${index + 1}`
                }
              />
            ))}

          </div>
        )}

        {/* ======================================================
            ARROWS
        ====================================================== */}

        {slides.length > 1 && (
          <div className="vocational-controls">

            <button
              className="vocational-arrow"
              onClick={previousSlide}
              aria-label="Previous slide"
            >
              <FaChevronLeft />
            </button>

            <button
              className="vocational-arrow"
              onClick={nextSlide}
              aria-label="Next slide"
            >
              <FaChevronRight />
            </button>

          </div>
        )}

      </header>
    </>
  );
};

export default HomeHeader;

